import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Trophy, Mail, Lock, User, AlertCircle, Loader2, X, Phone } from 'lucide-react';
import { CachedImage } from './CachedImage';

interface AuthFormProps {
  isOpen?: boolean;
  onClose?: () => void;
  onAuthSuccess?: () => void;
  initialMode?: 'signin' | 'signup';
}

export const AuthForm: React.FC<AuthFormProps> = ({
  isOpen = true,
  onClose,
  onAuthSuccess,
  initialMode = 'signin'
}) => {
  const { organizationSlug } = useParams<{ organizationSlug: string }>();
  const [isSignUp, setIsSignUp] = useState(initialMode === 'signup');

  // Update isSignUp when initialMode changes
  useEffect(() => {
    setIsSignUp(initialMode === 'signup');
  }, [initialMode]);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const { signUp, signIn, signInWithGoogle, sendPasswordResetEmail, isDemoMode, currentOrganization } = useAuth();

  // Don't render if not open (for modal mode)
  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { error } = isSignUp
        ? await signUp(email, password, firstName, lastName, organizationSlug, phoneNumber || undefined)
        : await signIn(email, password);

      if (error) {
        const rawMsg = (error.message || '').toLowerCase();

        if (isSignUp && (
          rawMsg.includes('already registered') ||
          rawMsg.includes('already exists') ||
          rawMsg.includes('user already') ||
          rawMsg.includes('email address is already') ||
          (error as any).code === 'user_already_exists'
        )) {
          setError(
            "An account with this email may already exist. Try signing in instead — or use “Forgot password?” if you can’t remember your password. If you’ve never signed up, double-check the email address for typos."
          );
        } else if (!isSignUp && (
          rawMsg.includes('invalid login') ||
          rawMsg.includes('invalid credentials') ||
          (error as any).code === 'invalid_credentials'
        )) {
          setError(
            "We couldn’t sign you in with that email and password. If you don’t have an account yet, tap “Sign up” below. Otherwise, use “Forgot password?” to reset it."
          );
        } else {
          setError(error.message);
        }
      } else {
        // Success - call the success callback
        onAuthSuccess?.();
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError(null);

    try {
      // Use the full current URL including query params for redirect
      const redirectTo = window.location.href;
      console.log('[AuthForm] Initiating Google sign-in, mode:', isSignUp ? 'signup' : 'signin', 'redirect:', redirectTo);

      const { error } = await signInWithGoogle(redirectTo);
      if (error) {
        console.error('[AuthForm] Google sign-in error:', error);
        setError(error.message);
        setGoogleLoading(false);
      } else {
        console.log('[AuthForm] Google sign-in initiated successfully, redirecting to Google...');
      }
      // Note: Don't call onAuthSuccess here or set loading to false
      // because the page will redirect to Google OAuth
    } catch (err) {
      console.error('[AuthForm] Unexpected Google sign-in error:', err);
      setError('An unexpected error occurred with Google sign-in');
      setGoogleLoading(false);
    }
  };

  const handleSendResetLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setResetError(null);

    try {
      const { error } = await sendPasswordResetEmail(email);
      if (error) {
        setResetError(error.message);
      } else {
        setEmailSent(true);
      }
    } catch (err) {
      setResetError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToSignIn = () => {
    setShowForgotPassword(false);
    setEmailSent(false);
    setResetError(null);
    setError(null);
  };
  return (
    <div className="fixed inset-0 z-[70] overflow-y-auto bg-black/50 backdrop-blur-sm">
      <div className="min-h-full w-full px-safe p-4 pt-safe pb-[calc(env(safe-area-inset-bottom)+6rem)] md:pb-safe flex items-start justify-center sm:items-center">
        <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-3xl shadow-2xl p-4 sm:p-6 lg:p-8 w-full max-w-md border border-gray-200/50 dark:border-gray-700/50 relative my-auto mt-4 sm:mt-0">
        {/* Close Button */}
        {onClose && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        )}

        {/* Header */}
        <div className="text-center mb-4 sm:mb-6">
          <div className="bg-gradient-to-br from-theme-primary to-theme-secondary p-4 rounded-2xl w-20 h-20 mx-auto mb-6 shadow-lg flex items-center justify-center">
            {currentOrganization?.logoUrl ? (
              <CachedImage
                src={currentOrganization.logoUrl}
                alt={currentOrganization.name}
                className="h-12 w-12 object-contain"
              />
            ) : (
              <Trophy className="h-12 w-12 text-white drop-shadow-sm" />
            )}
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300 bg-clip-text text-transparent mb-3">
            {currentOrganization?.name || 'FootyPicks'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-lg">
            {isSignUp ? 'Create your account to get started' : 'Sign in to your account'}
          </p>
        </div>

        {/* New user banner - only show in sign-in mode */}
        {!isSignUp && !showForgotPassword && (
          <div className="mb-4 bg-gradient-to-r from-theme-primary/10 to-theme-secondary/10 dark:from-theme-primary/20 dark:to-theme-secondary/20 border-2 border-theme-primary/30 dark:border-theme-primary/40 rounded-xl p-3 flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">First time here?</p>
              <p className="text-xs text-gray-600 dark:text-gray-300">You'll need to create an account first.</p>
            </div>
            <button
              type="button"
              onClick={() => {
                setIsSignUp(true);
                setError(null);
              }}
              className="shrink-0 bg-gradient-to-r from-theme-primary to-theme-secondary text-white px-4 py-2 rounded-lg font-semibold text-sm shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105"
            >
              Sign Up
            </button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-2.5 mb-4">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
              <p className="text-red-700 dark:text-red-300 text-sm font-medium">{error}</p>
            </div>
          </div>
        )}

        {/* Reset Error Message */}
        {resetError && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-2.5 mb-4">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
              <p className="text-red-700 dark:text-red-300 text-sm font-medium">{resetError}</p>
            </div>
          </div>
        )}

        {/* Forgot Password Form */}
        {showForgotPassword ? (
          <div className="space-y-4">
            {emailSent ? (
              <div className="text-center space-y-4">
                <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4 text-left">
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    <Mail className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                    <span className="font-semibold text-emerald-800 dark:text-emerald-300">Check your inbox</span>
                  </div>
                  <p className="text-emerald-700 dark:text-emerald-300 text-sm mb-2">
                    If an account exists for <strong>{email}</strong>, we’ve sent a password reset link. Click the link in the email to set a new password.
                  </p>
                  <p className="text-emerald-700 dark:text-emerald-300 text-xs">
                    Didn’t get an email after a few minutes? Check your spam folder, or make sure you typed the same email you signed up with. If you’ve never signed up, tap <strong>Back to Sign In</strong> and choose <strong>Sign up</strong>.
                  </p>
                </div>
                
                <button
                  onClick={handleBackToSignIn}
                  className="w-full bg-gradient-to-r from-theme-primary to-theme-secondary text-white py-2.5 px-6 rounded-xl font-semibold hover:from-theme-primary hover:to-theme-secondary transition-all duration-200 shadow-lg transform hover:scale-105"
                >
                  Back to Sign In
                </button>
              </div>
            ) : (
              <form onSubmit={handleSendResetLink} className="space-y-4">
                <div>
                  <label htmlFor="resetEmail" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
                    <input
                      id="resetEmail"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full pl-12 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm focus:ring-2 focus:ring-theme-primary focus:border-theme-primary transition-all duration-200 text-gray-900 dark:text-white font-medium"
                      placeholder="Enter your email address"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-theme-primary to-theme-secondary text-white py-2.5 px-6 rounded-xl font-semibold hover:from-theme-primary hover:to-theme-secondary focus:ring-2 focus:ring-theme-primary focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 shadow-lg shadow-theme-primary/25 transform hover:scale-105"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Sending Reset Link...</span>
                    </>
                  ) : (
                    <>
                      <Mail className="h-5 w-5" />
                      <span>Send Reset Link</span>
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleBackToSignIn}
                  className="w-full text-gray-600 dark:text-gray-400 py-2.5 px-6 rounded-xl font-medium hover:text-gray-800 dark:hover:text-gray-200 transition-colors duration-200"
                >
                  Back to Sign In
                </button>
              </form>
            )}
          </div>
        ) : (
          /* Regular Sign In/Sign Up Form */
          <form onSubmit={handleSubmit} className="space-y-4">
          {isSignUp && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                  First Name
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <input
                    id="firstName"
                    name="given-name"
                    type="text"
                    autoComplete="given-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required={isSignUp}
                    className="w-full pl-12 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-white font-medium"
                    placeholder="First name"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                  Last Name
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <input
                    id="lastName"
                    name="family-name"
                    type="text"
                    autoComplete="family-name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required={isSignUp}
                    className="w-full pl-12 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-white font-medium"
                    placeholder="Last name"
                  />
                </div>
              </div>
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
              <input
                id="email"
                name="email"
                type="email"
                autoComplete={isSignUp ? "email" : "username"}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full pl-12 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm focus:ring-2 focus:ring-theme-primary focus:border-theme-primary transition-all duration-200 text-gray-900 dark:text-white font-medium"
                placeholder="Enter your email"
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
              <input
                id="password"
                name="password"
                type="password"
                autoComplete={isSignUp ? "new-password" : "current-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full pl-12 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm focus:ring-2 focus:ring-theme-primary focus:border-theme-primary transition-all duration-200 text-gray-900 dark:text-white font-medium"
                placeholder="Enter your password"
              />
            </div>
            {isSignUp && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 font-medium">Password must be at least 6 characters</p>
            )}
          </div>

          {/* Phone Number - Optional, only show for sign up */}
          {isSignUp && (
            <div>
              <label htmlFor="phoneNumber" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                <span>Phone Number</span>
                <span className="text-gray-400 dark:text-gray-500 font-normal ml-1">(optional)</span>
              </label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
                <input
                  id="phoneNumber"
                  name="tel"
                  type="tel"
                  autoComplete="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="w-full pl-12 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm focus:ring-2 focus:ring-theme-primary focus:border-theme-primary transition-all duration-200 text-gray-900 dark:text-white font-medium"
                  placeholder="+44 7700 900000"
                />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">For WhatsApp updates about your picks</p>
            </div>
          )}

          {/* Forgot Password Link - Only show for sign in */}
          {!isSignUp && (
            <div className="text-right">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-theme-primary dark:text-theme-primary hover:text-theme-primary dark:hover:text-theme-primary font-medium text-sm transition-colors duration-200"
              >
                Forgot password?
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-theme-primary to-theme-secondary text-white py-2.5 px-6 rounded-xl font-semibold hover:from-theme-primary hover:to-theme-secondary focus:ring-2 focus:ring-theme-primary focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 shadow-lg shadow-theme-primary/25 transform hover:scale-105"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>{isSignUp ? 'Creating Account...' : 'Signing In...'}</span>
              </>
            ) : (
              <>
                <User className="h-5 w-5" />
                <span>{isSignUp ? 'Create Account' : 'Sign In'}</span>
              </>
            )}
          </button>
        </form>
        )}

        {/* Divider */}
        {!showForgotPassword && (
          <div className="my-4 flex items-center">
          <div className="flex-1 border-t border-gray-300 dark:border-gray-600"></div>
          <span className="px-6 text-sm text-gray-500 dark:text-gray-400 font-medium">or</span>
          <div className="flex-1 border-t border-gray-300 dark:border-gray-600"></div>
          </div>
        )}

        {/* Google Sign In */}
        {!showForgotPassword && (
          <button
          onClick={handleGoogleSignIn}
          disabled={googleLoading || loading}
          className="w-full bg-white/90 dark:bg-gray-700/90 backdrop-blur-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 py-2.5 px-6 rounded-xl font-semibold hover:bg-white dark:hover:bg-gray-700 hover:shadow-lg focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-3 transform hover:scale-105"
          >
          {googleLoading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Signing in with Google...</span>
            </>
          ) : (
            <>
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span>Continue with Google</span>
            </>
          )}
          </button>
        )}

        {/* Toggle Sign Up/Sign In */}
        {!showForgotPassword && (
          <div className="mt-4 text-center">
          {isSignUp ? (
            <p className="text-gray-600 dark:text-gray-400">
              Already have an account?
              <button
                type="button"
                onClick={() => { setIsSignUp(false); setError(null); }}
                className="ml-2 text-theme-primary dark:text-theme-primary hover:text-theme-primary dark:hover:text-theme-primary font-semibold transition-colors duration-200"
              >
                Sign In
              </button>
            </p>
          ) : (
            <button
              type="button"
              onClick={() => { setIsSignUp(true); setError(null); }}
              className="w-full border-2 border-theme-primary text-theme-primary dark:text-theme-primary py-2.5 px-6 rounded-xl font-semibold hover:bg-theme-primary/5 dark:hover:bg-theme-primary/10 transition-all duration-200"
            >
              Don't have an account? Sign Up
            </button>
          )}
          </div>
        )}

        {/* Demo Info */}
        {!showForgotPassword && isDemoMode && (
          <div className="mt-4 p-2.5 sm:p-3 bg-gradient-to-r from-theme-primary/10 to-theme-secondary/10 dark:from-theme-primary/20 dark:to-theme-secondary/20 rounded-xl border border-theme-primary/20 dark:border-theme-primary/50">
          <p className="text-theme-primary dark:text-theme-primary text-sm text-center font-medium">
            Demo mode is active - authentication is simulated for testing purposes.
          </p>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};